import { NgOptimizedImage } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    signal,
    ViewChild,
    WritableSignal
} from '@angular/core';
import { LogoLoadingComponent } from '../../../logo-loading/logo-loading.component';
import { HighlightTextPipe } from '../../pipes/highlight-text.pipe';
import { SanitizeHtmlPipe } from '../../pipes/sanitize-html.pipe';
import { CarouselBlockComponent } from './components/carousel-block/carousel-block.component';
import { HeroBlockConfiguration } from './models/hero-block.types';

@Component({
    selector: 'nw-hero-block',
    imports: [
        SanitizeHtmlPipe,
        LogoLoadingComponent,
        CarouselBlockComponent,
        NgOptimizedImage,
        HighlightTextPipe
    ],
    templateUrl: './hero-block.component.html',
    styleUrl: './hero-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeroBlockComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() config!: HeroBlockConfiguration;

    @ViewChild('contentText') contentText!: ElementRef;
    @ViewChild('media') media!: ElementRef;

    @HostBinding('class.nw-hero-block-media-only') mediaOnly: boolean = false;
    @HostBinding('class.nw-hero-block-light') light: boolean = false;
    @HostBinding('class.nw-hero-block-carousel-dark') carouselDark: boolean = false;
    @HostBinding('class.nw-hero-block-carousel-light') carouselLight: boolean = false;

    public dimensions$$: WritableSignal<{ width?: number; height?: number }> = signal({
        width: undefined,
        height: undefined
    });
    public loading$$: WritableSignal<boolean> = signal(true);

    private readonly MAX_MEDIA_HEIGHT: number = 548;
    private readonly MAX_MEDIA_WIDTH: number = 1280;
    private intervalId!: unknown;

    @HostListener('window:resize') resize(): void {
        if (this.mediaOnly) return;

        this.updateMediaSize();
    }

    public ngOnChanges(): void {
        this.mediaOnly =
            !this.config.content?.content &&
            !this.config.content?.button?.label &&
            !this.config.title?.content;

        this.light = this.config.scheme === 'light';
        this.carouselLight =
            this.config.scheme === 'light' && !!this.config.carousel?.length;
        this.carouselDark =
            this.config.scheme === 'dark' && !!this.config.carousel?.length;

        if (this.mediaOnly || this.config.carousel) {
            this.loading$$.set(false);
            return;
        }
        if (this.config.media?.type === 'image' && !!this.config.media.path) {
            this.getImageDimensions(this.config.media?.path)
                .then((dimensions: { width: number; height: number }) => {
                    const resizedDimensions = this.calculateAspectRatioFit(
                        dimensions.width,
                        dimensions.height,
                        this.MAX_MEDIA_WIDTH,
                        this.MAX_MEDIA_HEIGHT
                    );
                    this.dimensions$$.set(resizedDimensions);
                    this.loading$$.set(false);
                    setTimeout(() => this.updateMediaSize(), 100);
                })
                .catch((error) => console.error('Image load error', error));
        }
        if (this.config.media?.type === 'video' && !!this.config.media.path) {
            this.getVideoDimensions(this.config.media.path)
                .then((dimensions: { width: number; height: number }) => {
                    const resizedDimensions = this.calculateAspectRatioFit(
                        dimensions.width,
                        dimensions.height,
                        this.MAX_MEDIA_WIDTH,
                        this.MAX_MEDIA_HEIGHT
                    );
                    this.dimensions$$.set(resizedDimensions);
                    this.loading$$.set(false);
                    setTimeout(() => this.updateMediaSize(), 100);
                })
                .catch((error) => console.error('Video load error', error));
        }
    }

    public ngAfterViewInit(): void {
        this.forceAutoplay();
    }

    public ngOnDestroy(): void {
        clearInterval(this.intervalId as number);
    }

    private updateMediaSize(): void {
        if (!this.media?.nativeElement.clientWidth && !this.mediaOnly) return;

        const height =
            [].slice
                .call(this.contentText?.nativeElement.children || [0])
                .map((child: HTMLElement) => child.offsetHeight)
                ?.reduce(
                    (accumulator: number, currentValue: number) =>
                        accumulator + currentValue
                ) +
            48 +
            72;

        const resizedDimensions = this.calculateAspectRatioFit(
            this.dimensions$$().width || 0,
            this.dimensions$$().height || 0,
            this.media?.nativeElement.clientWidth,
            height < this.MAX_MEDIA_HEIGHT ? this.MAX_MEDIA_HEIGHT : height
        );
        this.dimensions$$.set(resizedDimensions);
    }

    private forceAutoplay(): void {
        if (this.config?.media?.type !== 'video') return;

        const MAX_ATTEMPTS: number = 10;
        let attempts: number = 0;

        this.intervalId = setInterval(() => {
            const video: HTMLVideoElement | null = document.getElementById(
                `${this.config.id}video`
            ) as HTMLVideoElement;

            if (!video && attempts < MAX_ATTEMPTS) {
                attempts++;
                return;
            }

            if (!video || attempts >= MAX_ATTEMPTS) {
                clearInterval(this.intervalId as number);
                return;
            }

            const promise = video.play();
            if (promise !== undefined) {
                promise
                    .then(() => {})
                    .catch(() => {
                        video.muted = true;
                        video.play();
                    });
            }
            attempts++;
        }, 1000);
    }

    private getImageDimensions(
        imageUrl: string
    ): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            this.loading$$.set(true);
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = reject;
            img.src = imageUrl;
        });
    }

    private getVideoDimensions(
        videoUrl: string
    ): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.onloadedmetadata = () => {
                resolve({ width: video.videoWidth, height: video.videoHeight });
            };
            video.onerror = reject;
            video.src = videoUrl;
        });
    }

    private calculateAspectRatioFit(
        originalWidth: number,
        originalHeight: number,
        targetWidth: number,
        minHeight: number = 0,
        maxHeight: number = this.MAX_MEDIA_HEIGHT
    ): { width: number; height: number } {
        const aspectRatio = originalHeight / originalWidth;
        let newWidth = targetWidth;
        let newHeight = targetWidth * aspectRatio;

        if (newHeight > maxHeight) {
            newHeight = maxHeight;
            newWidth = maxHeight / aspectRatio;
        } else if (newHeight < minHeight) {
            newHeight = minHeight;
            newWidth = minHeight / aspectRatio;
        }

        return { width: newWidth, height: newHeight };
    }
}
